/**
 * Passive Patrol Behavior
 * 
 * NPC patrols back and forth on spline.
 * Does not attack even when player is detected.
 * Uses passive detection (no combat response).
 */

import type { NPCBehavior, NPCBehaviorConfig } from './NPCBehavior';
import type { PatrolState } from '../presets/movement';
import { patrolTick } from '../presets/movement';
import { passiveDetect, defaultDeath } from '../presets/combat';

/**
 * Create a passive patrol behavior.
 * 
 * @param config - Configuration from map annotations
 * @returns NPCBehavior instance with closure state
 */
export const createPassivePatrol = (config: NPCBehaviorConfig): NPCBehavior => {
  // Calculate T bounds from meters (if splineLength provided)
  const splineLength = config.splineLength ?? 10; // Default 10m spline
  const spawnT = config.spawnT ?? 0.5;
  
  const leftT = spawnT - (config.patrolLeft ?? 2) / splineLength;
  const rightT = spawnT + (config.patrolRight ?? 2) / splineLength;
  
  // Closure state - create directly for simple T bounds
  const patrolState: PatrolState = {
    direction: 1,
    minT: Math.max(0, leftT),
    maxT: Math.min(1, rightT),
  };
  
  return {
    id: 'passive_patrol',
    displayName: 'Passive Patrol',
    
    onTick: (api) => {
      patrolTick(api, patrolState);
    },
    
    onPlayerDetected: passiveDetect,
    
    onPlayerLost: (_api) => {
      // Just resume normal patrol - nothing special to do
    },
    
    onDeath: defaultDeath,
    
    onSpawn: (_api) => {
      // Initialize at spawn position
      patrolState.direction = 1;
    },
  };
};
